<template>
    <div>
        <div class="custom-control custom-checkbox">
            <input :value="!!params.required"
                   :checked="!!params.required"
                   @change="$emit('update', {path: 'required', value: !params.required})"
                   :disabled="!enabled"
                   class="custom-control-input"
                   type="checkbox"
                   :id="`${extension.name}-required`">
            <label class="custom-control-label pt-1" :for="`${extension.name}-required`">
                Required to process
            </label>
        </div>

        <div style="max-width: 400px">
            <div class="mt-3">
                <label>
                    Autofill
                </label>
                <AppRadioButtonsGroup
                    label="label"
                    trackBy="key"
                    :disabled="!enabled"
                    :value="params.autofill"
                    :options="matchingExtensionAutofillInfo"
                    @update="value => $emit('update', {path: 'autofill', value})"/>
            </div>

            <div v-if="showForceOverrideToggle" class="mt-2">
                <AppSwitch :value="forceOverride"
                           @toggle="$emit('update', {path: 'forceOverride', value: !forceOverride})">
                    <div slot="label">
                        Override when value is empty
                    </div>
                </AppSwitch>
            </div>

            <div class="mt-3">
                <label>
                    Level
                </label>
                <AppRadioButtonsGroup
                    label="label"
                    trackBy="key"
                    :disabled="!enabled"
                    :value="params.level"
                    :options="matchingExtensionProcessLevelInfo"
                    @update="value => $emit('update', {path: 'level', value})"/>
            </div>
            <template v-if="params.level === MatchingExtensionProcessLevel.LINES">
                <div class="custom-control custom-checkbox mt-3">
                    <input :value="!!params.allLinesShouldBeMatched"
                           :checked="!!params.allLinesShouldBeMatched"
                           @change="$emit('update', {path: 'allLinesShouldBeMatched', value: !params.allLinesShouldBeMatched})"
                           :disabled="!enabled"
                           class="custom-control-input"
                           type="checkbox"
                           :id="`${extension.name}-allLinesShouldBeMatched`">
                    <label class="custom-control-label pt-1"
                           :for="`${extension.name}-allLinesShouldBeMatched`">
                        All lines should be matched
                    </label>
                </div>

                <div class="mt-2">
                    <label v-tooltip="'Select the properties to be used for automatic matching'">
                        Match lines on
                        <span
                            v-tooltip="`If multiple options are selected, lines will be matched if at least one condition is met.<br>
                                        If no options are selected, lines will be matched by ID.`"
                            class="pl-2">
                            <i class="fal fa-info-circle"></i>
                        </span>
                    </label>
                    <div v-for="matchInfo in matchLinesByInfo">
                        <div class="custom-control custom-checkbox">
                            <input :value="!!matchLinesBy[matchInfo.key]"
                                   :checked="!!matchLinesBy[matchInfo.key]"
                                   @change="onLinesMatchByToggle(matchInfo.key)"
                                   :disabled="!enabled"
                                   class="custom-control-input"
                                   type="checkbox"
                                   :id="`${extension.name}-lines-match-by-${matchInfo.key}`">
                            <label class="custom-control-label pt-1"
                                   :for="`${extension.name}-lines-match-by-${matchInfo.key}`">
                                {{ matchInfo.label }}
                            </label>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else-if="params.level === MatchingExtensionProcessLevel.DOCUMENT">
                <div class="custom-control custom-checkbox mt-3">
                    <input :value="!!params.totalDeviationCheckRequiredToProcess"
                           :checked="!!params.totalDeviationCheckRequiredToProcess"
                           @change="$emit('update', {path: 'totalDeviationCheckRequiredToProcess', value: !params.totalDeviationCheckRequiredToProcess})"
                           :disabled="!enabled"
                           class="custom-control-input"
                           type="checkbox"
                           :id="`${extension.name}-totalDeviationCheckRequiredToProcess`">
                    <label class="custom-control-label pt-1"
                           :for="`${extension.name}-totalDeviationCheckRequiredToProcess`">
                        Check total deviation
                        <span v-tooltip="{
                            content: `
                                If this is enabled, then the document cannot be processed with a total
                                <b class='text-purple'>deviation %</b> greater than the specified value.
                                <div class='mt-2'>Unallocated amount / Document total < <b class='text-purple'>deviation %</b> / 100</div>
                            `}">
                            <i class="fal fa-info-circle"></i>
                        </span>
                    </label>
                </div>
                <div v-if="params.totalDeviationCheckRequiredToProcess" class="form-group mt-1">
                    <label>Deviation %</label>
                    <vue-number-input
                        :value="params.totalDeviationPercentAllowed || MATCHING_EXTENSION_DEFAULT_TOTAL_DEVIATION_PERCENT"
                        @change="v => $emit('update', {path: 'totalDeviationPercentAllowed', value: v})"
                        :min="0" :max="Infinity"
                        controls
                        size="small">
                    </vue-number-input>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import AppRadioButtonsGroup from '../../../../../../components/shared/AppRadioButtonsGroup'
import {
    MATCHING_EXTENSION_DEFAULT_TOTAL_DEVIATION_PERCENT,
    MatchingExtensionAutofill,
    matchingExtensionAutofillInfo,
    MatchingExtensionProcessLevel,
    matchingExtensionProcessLevelInfo,
    matchLinesByInfo
} from '@/types/document-viewer/extension/matching'
import {FormExtensions} from '@/types/document-viewer/extension'
import AppSwitch from '@/components/shared/AppSwitch.vue'

export default {
    name: 'MatchingExtensionParams',
    components: {AppSwitch, AppRadioButtonsGroup},
    props: {
        params: {
            type: Object,
            required: true
        },
        enabled: {
            type: Boolean
        },
        extension: {
            required: true,
            default: () => ({})
        }
    },
    data() {
        return {
            MatchingExtensionProcessLevel,
            matchingExtensionAutofillInfo,
            matchingExtensionProcessLevelInfo,
            MATCHING_EXTENSION_DEFAULT_TOTAL_DEVIATION_PERCENT,
            matchLinesByInfo
        }
    },
    computed: {
        isPo() {
            return this.extension.name === FormExtensions.PURCHASE_ORDER
        },

        entityName() {
            return this.isPo ? 'PO' : 'Receipt'
        },

        matchLinesBy() {
            return this.params.matchLinesBy || {}
        },

        showForceOverrideToggle() {
            return [
                MatchingExtensionAutofill.ALWAYS,
                MatchingExtensionAutofill.ASK
            ].includes(this.params.autofill)
        },

        forceOverride() {
            return this.params.hasOwnProperty('forceOverride')
                ? this.params.forceOverride
                : true
        }
    },
    methods: {
        async onLinesMatchByToggle(key) {
            const value = !this.matchLinesBy[key]
            this.updateMatchLinesBy(key, value)

            await this.$nextTick()
            if (value) {
                if (key === 'unitPrice') {
                    this.updateMatchLinesBy('unitPriceAndQty', false)
                } else if (key === 'unitPriceAndQty') {
                    this.updateMatchLinesBy('unitPrice', false)
                }
            }
        },

        updateMatchLinesBy(key, value) {
            this.$emit('update', {path: ['matchLinesBy', key], value})
        }
    }
}
</script>
